import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Users, UserPlus, UserCheck, Clock, CheckCircle2,
  LayoutDashboard, BookOpen, Settings, Search, Filter,
  ChevronRight, Mail, Phone, MapPin, Calendar, Music,
  Loader2, XCircle, MessageSquare, Package, Eye, RefreshCw, Send,
  Edit, Video, X, ChevronDown, ChevronUp, Trash2, GraduationCap, Gift,
  BarChart3, TrendingUp, IndianRupee, DollarSign
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, FunnelChart, Funnel, LabelList
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const SUBCONTINENT_COUNTRIES = ['india', 'nepal', 'sri lanka', 'bangladesh', 'pakistan', 'bhutan', 'maldives'];
const isSubcontinent = (country) => SUBCONTINENT_COUNTRIES.some(c => (country || '').toLowerCase().includes(c));
const getPricingTier = (student) => {
  const domestic = isSubcontinent(student?.country);
  return domestic
    ? { label: 'Subcontinent', currency: '₹', icon: IndianRupee, color: 'orange', rates: { 32: '20,000', 40: '25,000', 50: '31,250', 65: '40,625' }, suffix: '+GST' }
    : { label: 'International', currency: '$', icon: DollarSign, color: 'blue', rates: { 32: '420', 40: '525', 50: '656', 65: '853' }, suffix: '' };
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'status-pending' },
  { value: 'contacted', label: 'Contacted', color: 'status-contacted' },
  { value: 'package_proposed', label: 'Package Proposed', color: 'status-package_proposed' },
  { value: 'package_sent', label: 'Package Sent', color: 'status-contacted' },
  { value: 'enrolled', label: 'Enrolled', color: 'status-enrolled' },
  { value: 'declined', label: 'Declined', color: 'status-declined' },
];

const AdminSidebar = () => {
  return (
    <aside className="fixed left-0 top-0 h-screen w-64 admin-sidebar border-r border-[#2A2A2A] pt-20 hidden lg:block">
      <nav className="p-4 space-y-2">
        <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37]">
          <LayoutDashboard size={20} />
          <span>Dashboard</span>
        </Link>
        <Link to="/admin/courses" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <BookOpen size={20} />
          <span>Course Guide</span>
        </Link>
        <Link to="/admin/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <Settings size={20} />
          <span>Class Settings</span>
        </Link>
        <Link to="/admin/perks" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <Gift size={20} />
          <span>Perks & Tiers</span>
        </Link>
        <Link to="/admin/training" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <GraduationCap size={20} />
          <span>Staff Training</span>
        </Link>
      </nav>
      
      <div className="absolute bottom-4 left-4 right-4">
        <div className="p-4 rounded-lg border border-[#2A2A2A] bg-black/30">
          <p className="text-xs text-[#A1A1AA] mb-2">Public Forms</p>
          <Link to="/apply" target="_blank" className="text-[#D4AF37] text-sm hover:underline block mb-1">
            → New Students
          </Link>
          <Link to="/repackage" target="_blank" className="text-[#D4AF37] text-sm hover:underline block mb-1">
            → Existing Students
          </Link>
          <Link to="/returning" target="_blank" className="text-[#D4AF37] text-sm hover:underline block">
            → Returning Students
          </Link>
        </div>
      </div>
    </aside>
  );
};

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hourPackages, setHourPackages] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditingClasses, setIsEditingClasses] = useState(false);
  const [editedClassInterests, setEditedClassInterests] = useState([]);
  const [expandedClassroom, setExpandedClassroom] = useState(null);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, studentsRes, packagesRes, classroomsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/students`),
        axios.get(`${API}/hour-packages`),
        axios.get(`${API}/classrooms`)
      ]);
      setStats(statsRes.data);
      setStudents(studentsRes.data);
      setHourPackages(packagesRes.data);
      setClassrooms(classroomsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const updateStudentStatus = async (studentId, updates) => {
    setIsUpdating(true);
    try {
      await axios.patch(`${API}/students/${studentId}`, updates);
      toast.success('Student updated successfully');
      fetchData();
      setSelectedStudent(null);
    } catch (error) {
      console.error('Error updating student:', error);
      toast.error('Failed to update student');
    } finally {
      setIsUpdating(false);
    }
  };

  const startEditingClasses = () => {
    setEditedClassInterests([...(selectedStudent?.class_interests || [])]);
    setIsEditingClasses(true);
  };

  const toggleClassInterest = (classData, classroomName) => {
    const existingIndex = editedClassInterests.findIndex(
      ci => ci.class_id === classData.id
    );
    
    if (existingIndex >= 0) {
      setEditedClassInterests(prev => prev.filter(ci => ci.class_id !== classData.id));
    } else {
      setEditedClassInterests(prev => [...prev, {
        class_id: classData.id,
        class_name: classData.name,
        classroom_name: classroomName,
        prefer_recording: false
      }]);
    }
  };

  const toggleRecordingPreference = (classId) => {
    setEditedClassInterests(prev => prev.map(ci =>
      ci.class_id === classId ? { ...ci, prefer_recording: !ci.prefer_recording } : ci
    ));
  };

  const saveClassInterests = async () => {
    setIsUpdating(true);
    try {
      await axios.patch(`${API}/students/${selectedStudent.id}`, { 
        class_interests: editedClassInterests 
      });
      toast.success('Class interests updated successfully');
      setIsEditingClasses(false);
      fetchData();
      // Update selected student locally
      setSelectedStudent(prev => ({ ...prev, class_interests: editedClassInterests }));
    } catch (error) {
      console.error('Error updating class interests:', error);
      toast.error('Failed to update class interests');
    } finally {
      setIsUpdating(false);
    }
  };

  const cancelEditingClasses = () => {
    setIsEditingClasses(false);
    setEditedClassInterests([]);
    setExpandedClassroom(null);
  };

  const handleDeleteStudent = async () => {
    if (!studentToDelete) return;

    setIsDeleting(true);
    try {
      await axios.delete(`${API}/students/${studentToDelete.id}`);
      toast.success('Student deleted successfully from dashboard and Google Sheets');
      setStudentToDelete(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting student:', error);
      toast.error('Failed to delete student');
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedStudents.length === 0) return;

    setIsDeleting(true);
    try {
      const response = await axios.post(`${API}/students/bulk-delete`, selectedStudents);
      toast.success(`Successfully deleted ${response.data.deleted_count} student(s) from dashboard and Google Sheets`);
      setSelectedStudents([]);
      setShowBulkDeleteDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error deleting students:', error);
      toast.error('Failed to delete students');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = student.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          student.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || student.student_type === typeFilter;
    const matchesStatus = statusFilter === 'all' || student.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusConfig = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return (
      <span className={`px-2 py-1 rounded-full text-xs ${statusConfig.color}`}>
        {statusConfig.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4AF37]" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header variant="solid" />
      <AdminSidebar />
      
      <main className="lg:ml-64 pt-20 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl text-[#EDEDED] mb-2">Sales Dashboard</h1>
            <p className="text-[#A1A1AA]">Manage student applications and enrollment status</p>
          </div>

          {/* Stats Cards - Clickable for Filtering */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
            <div
              className={`bg-[#121212] border rounded-xl p-4 cursor-pointer transition-all ${
                typeFilter === 'all' ? 'border-[#D4AF37] shadow-lg shadow-[#D4AF37]/20' : 'border-[#2A2A2A] hover:border-[#D4AF37]/50'
              }`}
              onClick={() => setTypeFilter('all')}
              data-testid="stat-total"
            >
              <div className="flex items-center gap-3 mb-2">
                <Users className="text-[#D4AF37]" size={20} />
                <span className="text-[#A1A1AA] text-sm">Total</span>
              </div>
              <p className="font-heading text-3xl text-[#EDEDED]">{stats?.total_students || 0}</p>
            </div>

            <div
              className={`bg-[#121212] border rounded-xl p-4 cursor-pointer transition-all ${
                typeFilter === 'new' ? 'border-emerald-400 shadow-lg shadow-emerald-400/20' : 'border-[#2A2A2A] hover:border-emerald-400/50'
              }`}
              onClick={() => setTypeFilter('new')}
              data-testid="stat-new"
            >
              <div className="flex items-center gap-3 mb-2">
                <UserPlus className="text-emerald-400" size={20} />
                <span className="text-[#A1A1AA] text-sm">New</span>
              </div>
              <p className="font-heading text-3xl text-[#EDEDED]">{stats?.new_students || 0}</p>
            </div>

            <div
              className={`bg-[#121212] border rounded-xl p-4 cursor-pointer transition-all ${
                typeFilter === 'existing' ? 'border-blue-400 shadow-lg shadow-blue-400/20' : 'border-[#2A2A2A] hover:border-blue-400/50'
              }`}
              onClick={() => setTypeFilter('existing')}
              data-testid="stat-existing"
            >
              <div className="flex items-center gap-3 mb-2">
                <UserCheck className="text-blue-400" size={20} />
                <span className="text-[#A1A1AA] text-sm">Existing</span>
              </div>
              <p className="font-heading text-3xl text-[#EDEDED]">{stats?.existing_students || 0}</p>
            </div>

            <div
              className={`bg-[#121212] border rounded-xl p-4 cursor-pointer transition-all ${
                typeFilter === 'returning' ? 'border-purple-400 shadow-lg shadow-purple-400/20' : 'border-[#2A2A2A] hover:border-purple-400/50'
              }`}
              onClick={() => setTypeFilter('returning')}
              data-testid="stat-returning"
            >
              <div className="flex items-center gap-3 mb-2">
                <RefreshCw className="text-purple-400" size={20} />
                <span className="text-[#A1A1AA] text-sm">Returning</span>
              </div>
              <p className="font-heading text-3xl text-[#EDEDED]">{stats?.returning_students || 0}</p>
            </div>

            <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-4" data-testid="stat-pending">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="text-yellow-400" size={20} />
                <span className="text-[#A1A1AA] text-sm">Pending</span>
              </div>
              <p className="font-heading text-3xl text-[#EDEDED]">{stats?.pending_students || 0}</p>
            </div>

            <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-4" data-testid="stat-enrolled">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle2 className="text-green-400" size={20} />
                <span className="text-[#A1A1AA] text-sm">Enrolled</span>
              </div>
              <p className="font-heading text-3xl text-[#EDEDED]">{stats?.enrolled_students || 0}</p>
            </div>
          </div>

          {/* Analytics Charts */}
          {stats && (stats.funnel?.length > 0 || stats.status_breakdown?.length > 0 || stats.popular_classes?.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Conversion Funnel */}
              {stats.funnel?.length > 0 && (
                <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="text-[#D4AF37]" size={20} />
                    <h3 className="font-heading text-lg text-[#F5F5DC]">Conversion Funnel</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={stats.funnel} layout="vertical" margin={{ left: 20, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                      <XAxis type="number" tick={{ fill: '#A1A1AA', fontSize: 12 }} />
                      <YAxis dataKey="stage" type="category" tick={{ fill: '#A1A1AA', fontSize: 11 }} width={110} />
                      <Tooltip
                        contentStyle={{ background: '#1a1a1a', border: '1px solid #2A2A2A', borderRadius: '8px', color: '#EDEDED' }}
                        labelStyle={{ color: '#D4AF37' }}
                      />
                      <Bar dataKey="count" fill="#D4AF37" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Status Breakdown */}
              {stats.status_breakdown?.length > 0 && (
                <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <BarChart3 className="text-[#D4AF37]" size={20} />
                    <h3 className="font-heading text-lg text-[#F5F5DC]">Status Breakdown</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={stats.status_breakdown}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        dataKey="value"
                        nameKey="name"
                        paddingAngle={3}
                      >
                        {stats.status_breakdown.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: '#1a1a1a', border: '1px solid #2A2A2A', borderRadius: '8px', color: '#EDEDED' }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: '12px' }}
                        formatter={(value) => <span style={{ color: '#A1A1AA' }}>{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Popular Classes */}
              {stats.popular_classes?.length > 0 && (
                <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-6 lg:col-span-2">
                  <div className="flex items-center gap-2 mb-4">
                    <Music className="text-[#D4AF37]" size={20} />
                    <h3 className="font-heading text-lg text-[#F5F5DC]">Most Popular Classes</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={stats.popular_classes} margin={{ bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                      <XAxis
                        dataKey="name"
                        tick={{ fill: '#A1A1AA', fontSize: 10 }}
                        angle={-35}
                        textAnchor="end"
                        interval={0}
                        height={80}
                      />
                      <YAxis tick={{ fill: '#A1A1AA', fontSize: 12 }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ background: '#1a1a1a', border: '1px solid #2A2A2A', borderRadius: '8px', color: '#EDEDED' }}
                        labelStyle={{ color: '#D4AF37' }}
                      />
                      <Bar dataKey="students" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {/* Active Filter Info */}
          {(typeFilter !== 'all' || statusFilter !== 'all' || searchTerm) && (
            <div className="mb-4 flex items-center gap-2 flex-wrap">
              <span className="text-[#A1A1AA] text-sm">Active filters:</span>
              {typeFilter !== 'all' && (
                <Badge
                  className={`cursor-pointer ${
                    typeFilter === 'new' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-400/50' :
                    typeFilter === 'returning' ? 'bg-purple-500/20 text-purple-400 border-purple-400/50' :
                    'bg-blue-500/20 text-blue-400 border-blue-400/50'
                  }`}
                  onClick={() => setTypeFilter('all')}
                >
                  {typeFilter === 'new' ? 'New' : typeFilter === 'returning' ? 'Returning' : 'Existing'}
                  <X size={12} className="ml-1" />
                </Badge>
              )}
              {statusFilter !== 'all' && (
                <Badge
                  className="cursor-pointer bg-yellow-500/20 text-yellow-400 border-yellow-400/50"
                  onClick={() => setStatusFilter('all')}
                >
                  {STATUS_OPTIONS.find(s => s.value === statusFilter)?.label}
                  <X size={12} className="ml-1" />
                </Badge>
              )}
              {searchTerm && (
                <Badge
                  className="cursor-pointer bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/50"
                  onClick={() => setSearchTerm('')}
                >
                  Search: "{searchTerm}"
                  <X size={12} className="ml-1" />
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTypeFilter('all');
                  setStatusFilter('all');
                  setSearchTerm('');
                }}
                className="text-[#A1A1AA] hover:text-[#EDEDED] text-xs"
              >
                Clear all
              </Button>
            </div>
          )}

          {/* Bulk Actions Toolbar */}
          {selectedStudents.length > 0 && (
            <div className="mb-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={selectedStudents.length === filteredStudents.length}
                  onCheckedChange={toggleSelectAll}
                  className="border-[#D4AF37]"
                />
                <span className="text-[#D4AF37] font-medium">
                  {selectedStudents.length} student{selectedStudents.length !== 1 ? 's' : ''} selected
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedStudents([])}
                  className="border-[#2A2A2A] text-[#A1A1AA]"
                >
                  Clear Selection
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowBulkDeleteDialog(true)}
                  className="bg-red-500 text-white hover:bg-red-600"
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="mr-2" size={16} />
                  Delete Selected
                </Button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A1A1AA]" size={18} />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#121212] border-[#2A2A2A] text-[#EDEDED]"
                data-testid="search-input"
              />
            </div>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px] bg-[#121212] border-[#2A2A2A] text-[#EDEDED]" data-testid="filter-type">
                <SelectValue placeholder="Student Type" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                <SelectItem value="all" className="text-[#EDEDED]">All Types</SelectItem>
                <SelectItem value="new" className="text-[#EDEDED]">New Students</SelectItem>
                <SelectItem value="existing" className="text-[#EDEDED]">Existing Students</SelectItem>
                <SelectItem value="returning" className="text-[#EDEDED]">Returning Students</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] bg-[#121212] border-[#2A2A2A] text-[#EDEDED]" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                <SelectItem value="all" className="text-[#EDEDED]">All Status</SelectItem>
                {STATUS_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value} className="text-[#EDEDED]">
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Students Table */}
          <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-[#2A2A2A] hover:bg-transparent">
                  <TableHead className="text-[#A1A1AA] w-12">
                    <Checkbox
                      checked={filteredStudents.length > 0 && selectedStudents.length === filteredStudents.length}
                      onCheckedChange={toggleSelectAll}
                      className="border-[#A1A1AA]"
                      data-testid="select-all-checkbox"
                    />
                  </TableHead>
                  <TableHead className="text-[#A1A1AA]">Student</TableHead>
                  <TableHead className="text-[#A1A1AA]">Type</TableHead>
                  <TableHead className="text-[#A1A1AA]">Status</TableHead>
                  <TableHead className="text-[#A1A1AA]">Classes</TableHead>
                  <TableHead className="text-[#A1A1AA]">Package</TableHead>
                  <TableHead className="text-[#A1A1AA]">Date</TableHead>
                  <TableHead className="text-[#A1A1AA]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStudents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-[#A1A1AA] py-8">
                      No students found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStudents.map(student => (
                    <TableRow key={student.id} className="border-[#2A2A2A] hover:bg-white/5">
                      <TableCell>
                        <Checkbox
                          checked={selectedStudents.includes(student.id)}
                          onCheckedChange={() => toggleStudentSelection(student.id)}
                          className="border-[#A1A1AA]"
                          data-testid={`select-student-${student.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-[#EDEDED] font-medium">{student.full_name}</p>
                          <p className="text-[#A1A1AA] text-sm">{student.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" 
                               className={
                                 student.student_type === 'new' ? 'bg-emerald-500/20 text-emerald-400' : 
                                 student.student_type === 'returning' ? 'bg-purple-500/20 text-purple-400' :
                                 'bg-blue-500/20 text-blue-400'
                               }>
                          {student.student_type === 'new' ? 'New' : student.student_type === 'returning' ? 'Returning' : 'Existing'}
                        </Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(student.status)}</TableCell>
                      <TableCell>
                        <span className="text-[#EDEDED]">{student.class_interests?.length || 0}</span>
                        <span className="text-[#A1A1AA]"> classes</span>
                      </TableCell>
                      <TableCell>
                        {student.proposed_package ? (
                          <span className="text-[#D4AF37]">{student.proposed_package}h</span>
                        ) : (
                          <span className="text-[#A1A1AA]">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-[#A1A1AA] text-sm">
                        {new Date(student.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedStudent(student)}
                            className="text-[#D4AF37] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                            data-testid={`view-student-${student.id}`}
                          >
                            <Eye size={16} />
                          </Button>
                          <Link to={`/admin/package/${student.id}`}>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-[#A1A1AA] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                              data-testid={`package-student-${student.id}`}
                            >
                              <Package size={16} />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setStudentToDelete(student)}
                            className="text-[#A1A1AA] hover:text-red-400 hover:bg-red-400/10"
                            data-testid={`delete-student-${student.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={() => setShowBulkDeleteDialog(false)}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-[#EDEDED] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#F5F5DC] flex items-center gap-2">
              <Trash2 className="text-red-400" size={24} />
              Delete Multiple Students
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <p className="text-[#A1A1AA]">
              Are you sure you want to delete <span className="text-[#D4AF37] font-bold">{selectedStudents.length}</span> student{selectedStudents.length !== 1 ? 's' : ''}?
              This action cannot be undone and will remove data from both the dashboard and Google Sheets.
            </p>

            <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-lg">
              <p className="text-red-400 text-sm font-medium flex items-center gap-2">
                <XCircle size={16} />
                Warning: This will permanently delete:
              </p>
              <ul className="text-red-300 text-sm mt-2 ml-6 list-disc">
                <li>Student records from the database</li>
                <li>All form submission data from Google Sheets</li>
                <li>Associated class interests and preferences</li>
              </ul>
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => setShowBulkDeleteDialog(false)}
                disabled={isDeleting}
                className="border-[#2A2A2A] text-[#A1A1AA]"
                data-testid="cancel-bulk-delete"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkDelete}
                disabled={isDeleting}
                className="bg-red-500 text-white hover:bg-red-600"
                data-testid="confirm-bulk-delete"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={16} />
                    Deleting {selectedStudents.length} student{selectedStudents.length !== 1 ? 's' : ''}...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2" size={16} />
                    Delete {selectedStudents.length} Student{selectedStudents.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!studentToDelete} onOpenChange={() => setStudentToDelete(null)}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-[#EDEDED] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#F5F5DC] flex items-center gap-2">
              <Trash2 className="text-red-400" size={24} />
              Delete Student
            </DialogTitle>
          </DialogHeader>

          {studentToDelete && (
            <div className="space-y-4 mt-4">
              <p className="text-[#A1A1AA]">
                Are you sure you want to delete this student? This action cannot be undone.
              </p>

              <div className="bg-black/30 p-4 rounded-lg">
                <p className="text-[#EDEDED] font-medium">{studentToDelete.full_name}</p>
                <p className="text-[#A1A1AA] text-sm">{studentToDelete.email}</p>
                <Badge variant="secondary"
                       className={`mt-2 ${
                         studentToDelete.student_type === 'new' ? 'bg-emerald-500/20 text-emerald-400' :
                         studentToDelete.student_type === 'returning' ? 'bg-purple-500/20 text-purple-400' :
                         'bg-blue-500/20 text-blue-400'
                       }`}>
                  {studentToDelete.student_type === 'new' ? 'New' :
                   studentToDelete.student_type === 'returning' ? 'Returning' : 'Existing'}
                </Badge>
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button
                  variant="outline"
                  onClick={() => setStudentToDelete(null)}
                  disabled={isDeleting}
                  className="border-[#2A2A2A] text-[#A1A1AA]"
                  data-testid="cancel-delete"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteStudent}
                  disabled={isDeleting}
                  className="bg-red-500 text-white hover:bg-red-600"
                  data-testid="confirm-delete"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={16} />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="mr-2" size={16} />
                      Delete Student
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Student Detail Dialog */}
      <Dialog open={!!selectedStudent} onOpenChange={() => setSelectedStudent(null)}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-[#EDEDED] max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedStudent && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-[#F5F5DC]">
                  {selectedStudent.full_name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Contact Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 text-[#A1A1AA]">
                    <Mail size={16} />
                    <span>{selectedStudent.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#A1A1AA]">
                    <Phone size={16} />
                    <span>
                      {selectedStudent.phone_country_code || ''}{selectedStudent.phone_number}
                    </span>
                  </div>
                  {selectedStudent.city && (
                    <div className="flex items-center gap-2 text-[#A1A1AA]">
                      <MapPin size={16} />
                      <span>{selectedStudent.city}, {selectedStudent.country}</span>
                    </div>
                  )}
                </div>

                {/* Type & Background */}
                <div className="p-4 bg-black/30 rounded-lg">
                  <p className="text-[#A1A1AA] text-sm mb-2">Student Type</p>
                  <Badge className={selectedStudent.student_type === 'new' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}>
                    {selectedStudent.student_type === 'new' ? 'New Student' : 'Existing Student'}
                  </Badge>
                  
                  {selectedStudent.musical_journey && (
                    <div className="mt-4">
                      <p className="text-[#A1A1AA] text-sm mb-1">Musical Journey</p>
                      <p className="text-[#EDEDED]">{selectedStudent.musical_journey}</p>
                    </div>
                  )}
                  
                  {selectedStudent.current_hour_package && (
                    <div className="mt-4">
                      <p className="text-[#A1A1AA] text-sm mb-1">Current Package</p>
                      <p className="text-[#D4AF37] font-medium">{selectedStudent.current_hour_package} Hours/Semester</p>
                    </div>
                  )}
                </div>

                {/* Pricing Tier */}
                {(() => {
                  const tier = getPricingTier(selectedStudent);
                  const TierIcon = tier.icon;
                  return (
                    <div className={`p-4 bg-${tier.color}-500/5 border border-${tier.color}-500/20 rounded-lg`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <TierIcon size={16} className={`text-${tier.color}-400`} />
                          <span className="text-[#A1A1AA] text-sm">Pricing: </span>
                          <span className={`text-${tier.color}-400 text-sm font-medium`}>{tier.label}</span>
                        </div>
                        <div className="flex gap-3">
                          {Object.entries(tier.rates).map(([hrs, price]) => (
                            <div key={hrs} className="text-center">
                              <p className="text-[10px] text-[#A1A1AA]">{hrs}h</p>
                              <p className="text-xs text-[#EDEDED] font-medium">{tier.currency}{price}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      {tier.suffix && <p className="text-[10px] text-orange-400/70 text-right mt-1">{tier.suffix}</p>}
                    </div>
                  );
                })()}

                {/* Class Interests */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[#A1A1AA] text-sm">Selected Classes ({selectedStudent.class_interests?.length || 0})</p>
                    {!isEditingClasses && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={startEditingClasses}
                        className="text-[#D4AF37] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                        data-testid="edit-classes-btn"
                      >
                        <Edit size={14} className="mr-1" />
                        Edit
                      </Button>
                    )}
                  </div>
                  
                  {isEditingClasses ? (
                    <div className="space-y-4">
                      {/* Class Editor */}
                      <div className="max-h-80 overflow-y-auto space-y-2 bg-black/20 p-3 rounded-lg">
                        {classrooms.map(classroom => (
                          <div key={classroom.id} className="border border-[#2A2A2A] rounded-lg overflow-hidden">
                            <button
                              onClick={() => setExpandedClassroom(expandedClassroom === classroom.id ? null : classroom.id)}
                              className="w-full flex items-center justify-between p-3 text-left hover:bg-white/5"
                            >
                              <span className="text-[#F5F5DC] font-medium">{classroom.name}</span>
                              {expandedClassroom === classroom.id ? (
                                <ChevronUp size={16} className="text-[#A1A1AA]" />
                              ) : (
                                <ChevronDown size={16} className="text-[#A1A1AA]" />
                              )}
                            </button>
                            
                            {expandedClassroom === classroom.id && (
                              <div className="p-3 pt-0 space-y-2">
                                {classroom.classes?.map(cls => {
                                  const isSelected = editedClassInterests.some(ci => ci.class_id === cls.id);
                                  const classInterest = editedClassInterests.find(ci => ci.class_id === cls.id);
                                  
                                  return (
                                    <div key={cls.id} className="p-2 rounded bg-black/30">
                                      <div 
                                        className="flex items-start gap-2 cursor-pointer"
                                        onClick={() => toggleClassInterest(cls, classroom.name)}
                                      >
                                        <Checkbox 
                                          checked={isSelected}
                                          className="mt-0.5"
                                        />
                                        <div className="flex-1">
                                          <span className="text-[#EDEDED] text-sm">{cls.name}</span>
                                        </div>
                                      </div>
                                      
                                      {isSelected && (
                                        <div className="ml-6 mt-2">
                                          <label 
                                            className="flex items-center gap-2 cursor-pointer text-xs text-[#A1A1AA]"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleRecordingPreference(cls.id);
                                            }}
                                          >
                                            <Checkbox 
                                              checked={classInterest?.prefer_recording || false}
                                            />
                                            <Video size={12} className="text-[#D4AF37]" />
                                            <span>Recording only</span>
                                          </label>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {/* Selected Summary */}
                      {editedClassInterests.length > 0 && (
                        <div className="bg-[#D4AF37]/10 p-3 rounded-lg">
                          <p className="text-[#D4AF37] text-sm mb-2 font-medium">
                            Selected ({editedClassInterests.length})
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {editedClassInterests.map(ci => (
                              <span 
                                key={ci.class_id}
                                className="px-2 py-0.5 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-xs inline-flex items-center gap-1"
                              >
                                {ci.class_name}
                                {ci.prefer_recording && <Video size={10} />}
                                <button 
                                  onClick={() => toggleClassInterest({ id: ci.class_id, name: ci.class_name }, ci.classroom_name)}
                                  className="hover:text-white"
                                >
                                  <X size={12} />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={cancelEditingClasses}
                          className="border-[#2A2A2A] text-[#A1A1AA]"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={saveClassInterests}
                          disabled={isUpdating}
                          className="bg-[#D4AF37] text-black hover:bg-[#D4AF37]/90"
                          data-testid="save-classes-btn"
                        >
                          {isUpdating ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {selectedStudent.class_interests?.length > 0 ? (
                        selectedStudent.class_interests.map((ci, idx) => (
                          <span key={idx} className="px-3 py-1 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] text-sm">
                            {ci.class_name}
                            {ci.prefer_recording && ' (Recording)'}
                          </span>
                        ))
                      ) : (
                        <span className="text-[#A1A1AA] text-sm">No classes selected</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Goals */}
                {selectedStudent.goals && (
                  <div>
                    <p className="text-[#A1A1AA] text-sm mb-2">Goals</p>
                    <p className="text-[#EDEDED] bg-black/30 p-3 rounded-lg">{selectedStudent.goals}</p>
                  </div>
                )}

                {/* Schedule */}
                <div className="grid md:grid-cols-2 gap-4">
                  {selectedStudent.preferred_days?.length > 0 && (
                    <div>
                      <p className="text-[#A1A1AA] text-sm mb-2">Preferred Days</p>
                      <p className="text-[#EDEDED]">{selectedStudent.preferred_days.join(', ')}</p>
                    </div>
                  )}
                  {selectedStudent.preferred_times?.length > 0 && (
                    <div>
                      <p className="text-[#A1A1AA] text-sm mb-2">Preferred Times</p>
                      <p className="text-[#EDEDED]">{selectedStudent.preferred_times.join(', ')}</p>
                    </div>
                  )}
                </div>

                {/* Admin Controls */}
                <div className="border-t border-[#2A2A2A] pt-6 space-y-4">
                  <h3 className="font-heading text-lg text-[#F5F5DC]">Update Status</h3>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-[#A1A1AA] text-sm mb-2">Status</p>
                      <Select 
                        value={selectedStudent.status}
                        onValueChange={(value) => updateStudentStatus(selectedStudent.id, { status: value })}
                      >
                        <SelectTrigger className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]" data-testid="update-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                          {STATUS_OPTIONS.map(option => (
                            <SelectItem key={option.value} value={option.value} className="text-[#EDEDED]">
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <p className="text-[#A1A1AA] text-sm mb-2">Proposed Package</p>
                      <Select 
                        value={selectedStudent.proposed_package ? String(selectedStudent.proposed_package) : ''}
                        onValueChange={(value) => updateStudentStatus(selectedStudent.id, { proposed_package: parseInt(value) })}
                      >
                        <SelectTrigger className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]" data-testid="update-package">
                          <SelectValue placeholder="Select package" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                          {hourPackages.map(pkg => (
                            <SelectItem key={pkg.id} value={String(pkg.hours)} className="text-[#EDEDED]">
                              {pkg.hours} Hours/Semester
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <p className="text-[#A1A1AA] text-sm mb-2">Admin Notes</p>
                    <Textarea
                      placeholder="Add notes about this student..."
                      defaultValue={selectedStudent.admin_notes || ''}
                      className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]"
                      onBlur={(e) => {
                        if (e.target.value !== selectedStudent.admin_notes) {
                          updateStudentStatus(selectedStudent.id, { admin_notes: e.target.value });
                        }
                      }}
                      data-testid="admin-notes"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
